import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { KeyRound, CheckCircle, AlertCircle, User, Mail } from "lucide-react";
import { MfaSection } from "@/components/admin/MfaSection";
import { RecoveryCodesSection } from "@/components/admin/RecoveryCodesSection";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";
import { validateNewPassword } from "@/lib/password-strength";

export const Route = createFileRoute("/admin/profil")({
  head: () => ({
    meta: [
      { title: "Min profil - AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ProfilePage,
});

type Feedback = { type: "error" | "success"; message: string } | null;

function FeedbackBox({ feedback }: { feedback: Feedback }) {
  if (!feedback) return null;
  const isError = feedback.type === "error";
  return (
    <div
      className={
        isError
          ? "flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
          : "flex items-start gap-2 rounded-lg bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400"
      }
    >
      {isError ? <AlertCircle size={16} className="mt-0.5 shrink-0" /> : <CheckCircle size={16} className="mt-0.5 shrink-0" />}
      <span>{feedback.message}</span>
    </div>
  );
}

function ProfilePage() {
  // Profile (full name)
  const [userId, setUserId] = useState<string | null>(null);
  const [currentEmail, setCurrentEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [initialFullName, setInitialFullName] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileFeedback, setProfileFeedback] = useState<Feedback>(null);

  // Email
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<Feedback>(null);

  // Password
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState<Feedback>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!active || !user) return;
      setUserId(user.id);
      setCurrentEmail(user.email ?? "");

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!active) return;
      const name = profile?.full_name ?? "";
      setFullName(name);
      setInitialFullName(name);
    })();
    return () => { active = false; };
  }, []);

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileFeedback(null);
    if (!userId) return;
    const trimmed = fullName.trim();
    if (trimmed.length === 0) {
      setProfileFeedback({ type: "error", message: "Navn må ikke være tomt" });
      return;
    }
    if (trimmed.length > 100) {
      setProfileFeedback({ type: "error", message: "Navn må højst være 100 tegn" });
      return;
    }
    setProfileLoading(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: trimmed })
      .eq("user_id", userId);
    setProfileLoading(false);
    if (error) {
      setProfileFeedback({ type: "error", message: error.message || "Kunne ikke opdatere navnet" });
      return;
    }
    setInitialFullName(trimmed);
    setFullName(trimmed);
    setProfileFeedback({ type: "success", message: "Navnet blev opdateret." });
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailFeedback(null);
    const trimmed = newEmail.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setEmailFeedback({ type: "error", message: "Indtast en gyldig email-adresse" });
      return;
    }
    if (trimmed.length > 255) {
      setEmailFeedback({ type: "error", message: "Email må højst være 255 tegn" });
      return;
    }
    if (trimmed === currentEmail.toLowerCase()) {
      setEmailFeedback({ type: "error", message: "Den nye email er den samme som den nuværende" });
      return;
    }
    setEmailLoading(true);
    const { error } = await supabase.auth.updateUser(
      { email: trimmed },
      { emailRedirectTo: `${window.location.origin}/admin/profil` },
    );
    setEmailLoading(false);
    if (error) {
      setEmailFeedback({ type: "error", message: error.message || "Kunne ikke opdatere email" });
      return;
    }
    setEmailFeedback({
      type: "success",
      message: `Vi har sendt en bekræftelsesmail til både ${currentEmail} og ${trimmed}. Begg adresser skal bekræftes for at fuldføre ændringen.`,
    });
    setNewEmail("");
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordFeedback(null);
    if (!currentPassword) {
      setPasswordFeedback({ type: "error", message: "Indtast din nuværende adgangskode" });
      return;
    }
    const strengthError = validateNewPassword(password);
    if (strengthError) {
      setPasswordFeedback({ type: "error", message: strengthError });
      return;
    }
    if (password === currentPassword) {
      setPasswordFeedback({ type: "error", message: "Den nye adgangskode må ikke være den samme som den nuværende" });
      return;
    }
    if (password !== confirmPassword) {
      setPasswordFeedback({ type: "error", message: "Adgangskoderne stemmer ikke overens" });
      return;
    }
    if (!currentEmail) {
      setPasswordFeedback({ type: "error", message: "Kunne ikke verificere bruger. Log ind igen." });
      return;
    }
    setPasswordLoading(true);
    // Verify current password by attempting a sign-in with it
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: currentEmail,
      password: currentPassword,
    });
    if (verifyError) {
      setPasswordLoading(false);
      setPasswordFeedback({ type: "error", message: "Den nuværende adgangskode er forkert" });
      return;
    }
    const { error } = await supabase.auth.updateUser({ password });
    setPasswordLoading(false);
    if (error) {
      setPasswordFeedback({ type: "error", message: error.message || "Kunne ikke opdatere adgangskoden" });
      return;
    }
    setPasswordFeedback({ type: "success", message: "Adgangskoden blev opdateret." });
    setCurrentPassword("");
    setPassword("");
    setConfirmPassword("");
  };

  const nameUnchanged = fullName.trim() === initialFullName.trim();

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Min profil</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Administrer dit navn, din email-adresse og adgangskode.
        </p>
      </div>

      {/* Full name */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User size={18} /> Fulde navn
          </CardTitle>
          <CardDescription>Navnet vises sammen med din profil i admin.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveName} className="space-y-4">
            <FeedbackBox feedback={profileFeedback} />
            <div className="space-y-2">
              <Label htmlFor="fullName">Navn</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Fornavn Efternavn"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                maxLength={100}
                required
                autoComplete="name"
              />
            </div>
            <Button
              type="submit"
              disabled={profileLoading || nameUnchanged}
              className="bg-accent text-accent-foreground hover:brightness-110"
            >
              {profileLoading ? "Gemmer..." : "Gem navn"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail size={18} /> Email-adresse
          </CardTitle>
          <CardDescription>
            Nuværende: <span className="font-medium text-foreground">{currentEmail || "—"}</span>. Ændringen kræver bekræftelse via mail.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangeEmail} className="space-y-4">
            <FeedbackBox feedback={emailFeedback} />
            <div className="space-y-2">
              <Label htmlFor="newEmail">Ny email-adresse</Label>
              <Input
                id="newEmail"
                type="email"
                placeholder="ny@eksempel.dk"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                maxLength={255}
                required
                autoComplete="email"
              />
            </div>
            <Button
              type="submit"
              disabled={emailLoading}
              className="bg-accent text-accent-foreground hover:brightness-110"
            >
              {emailLoading ? "Sender bekræftelse..." : "Skift email"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <KeyRound size={18} /> Skift adgangskode
          </CardTitle>
          <CardDescription>
            Bekræft din nuværende adgangskode og vælg en ny på mindst 8 tegn med store og små bogstaver samt tal.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <FeedbackBox feedback={passwordFeedback} />
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Nuværende adgangskode</Label>
              <Input
                id="currentPassword"
                type="password"
                placeholder="Din nuværende adgangskode"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Ny adgangskode</Label>
              <Input
                id="password"
                type="password"
                placeholder="Mindst 8 tegn, store/små bogstaver + tal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <PasswordStrengthMeter password={password} confirmPassword={confirmPassword} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Bekræft ny adgangskode</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Gentag den nye adgangskode"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              disabled={passwordLoading}
              className="bg-accent text-accent-foreground hover:brightness-110"
            >
              {passwordLoading ? "Opdaterer..." : "Opdater adgangskode"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* MFA / 2FA */}
      <MfaSection />

      {/* Recovery codes */}
      <RecoveryCodesSection />
    </div>
  );
}
